/**
 * `GET /api/feedback/availability` as a boolean (ADR-4, ADR-14). Loading, an error and a 404 are all
 * "not enabled", so the flag never flashes in and out, and a failure is silent: no toast, no
 * `console.error` (the smoke spec counts console errors).
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { fetchAvailability } from './feedbackApi';

export const AVAILABILITY_KEY = ['feedback', 'availability'] as const;

/** `enabled` gates the request: a user without the permission never asks (no needless 403 in the console). */
export function useFeedbackAvailability(enabled = true): boolean {
  const { data } = useQuery({
    queryKey: AVAILABILITY_KEY,
    queryFn: fetchAvailability,
    staleTime: 5 * 60 * 1000,
    retry: false,
    enabled,
  });
  return enabled && data?.enabled === true;
}

/** Marks the cached answer stale, so the flag hides on the next check after a 404 (ADR-14). */
export function useInvalidateFeedbackAvailability(): () => void {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: AVAILABILITY_KEY });
  }, [queryClient]);
}
